import {AxiosInstance} from "axios";
import IndividualContact from "../model/contact/IndividualContact";
import IndividualContactData from "../model/contact/individual/IndividualContactData";
import AddressHistory from "../model/addressHistory/AddressHistory";
import IndividualContactAddressHistory from "../model/addressHistory/IndividualContactAddressHistory";
import {Option} from "ts-option";
import * as _ from "underscore";
import {IndividualContactApiDto} from "./model/rest";
import {ContactsMap} from "../store/reducer/contact/ContactsState";

/**
 * Api for contacts.
 */
export default class ContactApi {
	constructor(public readonly axiosInstance: AxiosInstance) { }

	/**
	 * Creates individual contact.
	 * @param {IndividualContactData} data Individual contact form data transfer object.
	 * @returns {Promise<IndividualContact>} Promise containing created individual contact.
	 */
	createIndividualContact(data: IndividualContactData): Promise<IndividualContact> {
		const url = `/api/web/individual_contacts`;
		return this.axiosInstance.post(url, data.toJson()).then(resp => IndividualContact.fromJson(resp.data));
	}

	/**
	 * Gets individual contacts.
	 * @returns {Promise<IndividualContact[]>} Promise containing array of individual contacts.
	 */
	fetchIndividualContacts(): Promise<ContactsMap> {
		const createMap = (contactsArray: IndividualContactApiDto[]) =>
			contactsArray.reduce((obj: ContactsMap, item) => {
				const contact = IndividualContact.fromJson(item);
				obj[contact.id] = contact;
				return obj;
			}, {});
		const url = `/api/web/individual_contacts`;
		return this.axiosInstance.get(url).then(resp => createMap(resp.data));
	}

	/**
	 * Gets individual contact.
	 * @param {string} id individual contact identifier.
	 * @returns {Promise<IndividualContact>} Promise containing individual contact.
	 */
	fetchIndividualContact(id: string): Promise<IndividualContact> {
		const url = `/api/web/individual_contacts/${id}`;
		return this.axiosInstance.get(url).then(resp => IndividualContact.fromJson(resp.data));
	}

	/**
	 * Edit individual contact.
	 * @param {string} id individual contact identifier.
	 * @param {IndividualContactData} data Individual contact form data transfer object.
	 * @returns {Promise<IndividualContact>} Promise containing edited individual contact.
	 */
	editIndividualContact(id: string, data: IndividualContactData): Promise<IndividualContact> {
		const url = `/api/web/individual_contacts/${id}`;
		return this.axiosInstance.put(url, data.toJson()).then(resp => IndividualContact.fromJson(resp.data));
	}

	/**
	 * Fetch individual address history for single contact.
	 * @param {string} contactId contact identifier.
	 * @return {Promise<AddressHistory[]>} Promise containing individual contact address histories.
	 */
	fetchAddressHistory(contactId: string): Promise<IndividualContactAddressHistory> {
		const url = `/api/web/individual_contacts/${contactId}/site_history`;
		return this.axiosInstance.get(url).then(resp => IndividualContactAddressHistory.fromJson(resp.data, contactId));
	}

	/**
	 * Fetch individual address history for single contact.
	 * @param {IndividualContactAddressHistory} data contact address history.
	 * @return {Promise<AddressHistory[]>} Promise containing individual contact address histories.
	 */
	updateAddressHistory(data: IndividualContactAddressHistory): Promise<IndividualContactAddressHistory> {
		const url = `/api/web/individual_contacts/${data.contactId}/site_history`;
		return this.axiosInstance.put(url, data.toJson()).then(
			resp => IndividualContactAddressHistory.fromJson(resp.data, data.contactId)
		);
	}

	/**
	 * Updates address histories for given contacts.
	 * Updates histories only if they are given.
	 * @param {Option<IndividualContactAddressHistory>} landlordAddressHistories landlord address histories.
	 * @param {Option<IndividualContactAddressHistory>} billPayerAddressHistories bill payer address histories.
	 * @return {Promise<void>} return promise of the action.
	 */
	updateContactsAddressHistories(landlordAddressHistories: Option<IndividualContactAddressHistory>,
		billPayerAddressHistories: Option<IndividualContactAddressHistory>): Promise<IndividualContactAddressHistory[]> {
		const addressHistoriesPromises: Promise<IndividualContactAddressHistory>[] = [];
		const addressesDataToUpdate: IndividualContactAddressHistory[] = [];
		landlordAddressHistories.forEach(histories => addressesDataToUpdate.push(histories));
		billPayerAddressHistories.forEach(histories => addressesDataToUpdate.push(histories));
		_.uniq(addressesDataToUpdate, value => value.contactId).map(addressToUpdate => {
			addressHistoriesPromises.push(this.updateAddressHistory(addressToUpdate))
		});
		return Promise.all(addressHistoriesPromises);
	}

	/**
	 * Archive or unarchive contact.
	 * @param {string} contactId
	 * @param {boolean} archived
	 * @returns {AxiosPromise<any>}
	 */
	changeContactArchivedStatus(contactId: string, archived: boolean) {
		const url = `/api/web/individual_contacts/${contactId}/archive`;
		return this.axiosInstance.put(url, {archived});
	}
}
